import { type Candidate, type CandidateStatus } from "./types/candidate";
import { type Job, type JobStatus } from "./types/job";

/** Whether the admin may release a job's recommendation, and what to warn
 * about first (ADR 0004 in the repo root `docs/adr/`). */
export interface ReleaseCheck {
  canRelease: boolean;
  blockedReason: string | null;
  /** Recommended candidates without a recommendation text. A warning, not a block. */
  missingText: Candidate[];
}

export function releaseCheck(
  recommended: readonly Candidate[],
  jobStatus: JobStatus,
): ReleaseCheck {
  const missingText = recommended.filter((c) => !c.recommendationText?.trim());
  // Mirrors the backend: a release is only possible from Outreaching.
  if (jobStatus !== "outreaching") {
    return {
      canRelease: false,
      blockedReason: "Nur aus der Qualifizierung freigebbar",
      missingText,
    };
  }
  if (recommended.length === 0) {
    return { canRelease: false, blockedReason: "Mindestens ein Kandidat nötig", missingText };
  }
  return { canRelease: true, blockedReason: null, missingText };
}

/** Whether the recruiter may see the job's recommendation. Set by the
 * release and kept when the job is later closed (ADR 0004). */
export function isRecommendationReleased(job: Pick<Job, "recommendationReleasedAt">): boolean {
  return job.recommendationReleasedAt != null;
}

/** Status actions the UI must not offer, because the API refuses them: on a
 * closed job every candidate is frozen (#19), and a recruiter can't act on a
 * candidate in an unreleased recommendation, whose refusal would reveal the
 * draft (ADR 0004). */
export function statusActionsLocked(status: CandidateStatus, jobStatus: JobStatus): boolean {
  return jobStatus === "closed" || status === "in_review";
}
